import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  getDetailedSalaryReport,
  downloadDetailedSalaryReport,
} from '../../api';
import { TABLE_PAPER_SX, HEAD_CELL_SX, TABLE_ROW_SX, BODY_CELL_SX } from '../common/tableStyles';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const yearOptions = () => {
  const current = new Date().getFullYear();
  return [current, current - 1, current - 2, current - 3].map(String);
};

// Column groups — keys must match backend `columns[].key`
const GROUPS = [
  {
    label: 'Identity',
    bg: '#eef2ff',
    keys: ['employee_no', 'name', 'designation', 'department', 'team_head', 'manager_name'],
  },
  {
    label: 'Earnings',
    bg: '#ecfdf5',
    keys: [
      'basic', 'hra', 'conveyance', 'medical', 'special_allowance',
      'food_allowance', 'travel_allowance', 'bonus', 'incentive',
      'exam_increment', 'other_earnings',
    ],
  },
  {
    label: 'Deductions',
    bg: '#fef2f2',
    keys: [
      'employer_pf', 'employee_pf', 'esi', 'insurance', 'income_tax',
      'loan', 'other_deduction', 'staff_advance', 'penalty', 'lop_deduction',
    ],
  },
  {
    label: 'Totals',
    bg: '#fff7ed',
    keys: ['total_earnings', 'total_deductions', 'net_pay', 'monthly_ctc', 'annual_ctc'],
  },
];

const TEXT_KEYS = new Set(['employee_no', 'name', 'designation', 'department', 'team_head', 'manager_name']);

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const SOFT_INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };

const inrFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const groupBgFor = (key) => GROUPS.find((g) => g.keys.includes(key))?.bg || 'inherit';

const DetailedSalaryReport = () => {
  const [mode, setMode] = useState('structure');
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0); // MUI is 0-based, backend is 1-based
  const [pageSize, setPageSize] = useState(25);

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Debounce search input → committed search
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchInput]);

  const filters = useMemo(() => ({
    mode,
    month: mode === 'paid' ? month : undefined,
    year: mode === 'paid' ? year : undefined,
    search: search || undefined,
    page: page + 1,
    pageSize,
  }), [mode, month, year, search, page, pageSize]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getDetailedSalaryReport(filters);
        if (cancelled) return;
        setColumns(data.columns || []);
        setRows(data.results || []);
        setCount(data.count || 0);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message || 'Failed to load report');
        setRows([]);
        setCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDetailedSalaryReport({
        mode,
        month: mode === 'paid' ? month : undefined,
        year: mode === 'paid' ? year : undefined,
        search: search || undefined,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to download Excel');
    } finally {
      setDownloading(false);
    }
  };

  const renderCell = (key, value) => {
    if (TEXT_KEYS.has(key)) return value || '—';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return value ?? '—';
    return inrFormatter.format(num);
  };

  const stickyCellSx = (key, isHeader = false) => {
    if (key === 'employee_no') {
      return {
        position: 'sticky',
        left: 0,
        background: isHeader ? '#dbeafe' : '#fff',
        zIndex: isHeader ? 4 : 2,
        minWidth: 110,
        boxShadow: '1px 0 0 #e5e7eb',
      };
    }
    if (key === 'name') {
      return {
        position: 'sticky',
        left: 110,
        background: isHeader ? '#dbeafe' : '#fff',
        zIndex: isHeader ? 4 : 2,
        minWidth: 160,
        boxShadow: '1px 0 0 #e5e7eb',
      };
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ReceiptLongIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Detailed Salary Report
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Salary breakdown across earnings, deductions and totals.
              </Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            label={mode === 'paid' ? `Paid · ${month} ${year}` : 'Current Salary Structure'}
            sx={mode === 'paid'
              ? { fontWeight: 700, border: 'none', bgcolor: `${ACCENT}14`, color: ACCENT }
              : { fontWeight: 700, border: 'none', bgcolor: '#f3f4f6', color: '#6b7280' }}
          />
        </Box>
      </Paper>

      {/* Filters card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) { setMode(v); setPage(0); } }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 700, borderRadius: '12px',
              borderColor: '#e5e7eb', color: '#6b7280',
              '&.Mui-selected': { bgcolor: `${ACCENT}14`, color: ACCENT, '&:hover': { bgcolor: `${ACCENT}22` } },
            },
          }}
        >
          <ToggleButton value="structure">Current Structure</ToggleButton>
          <ToggleButton value="paid">Paid in Month</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 130, ...SOFT_INPUT_SX }} disabled={mode !== 'paid'}>
          <Select value={month} onChange={(e) => { setMonth(e.target.value); setPage(0); }} MenuProps={SELECTED_MENU_PROPS}>
            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100, ...SOFT_INPUT_SX }} disabled={mode !== 'paid'}>
          <Select value={year} onChange={(e) => { setYear(e.target.value); setPage(0); }} MenuProps={SELECTED_MENU_PROPS}>
            {yearOptions().map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search by Emp ID or name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260, ...SOFT_INPUT_SX }}
        />

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          disableElevation
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading || loading}
          sx={GRADIENT_BTN}
        >
          {downloading ? 'Preparing…' : 'Download Excel'}
        </Button>
      </Paper>

      {error && (
        <Paper elevation={0} sx={{ p: 2, mb: { xs: 2, md: 3 }, borderRadius: '16px', bgcolor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontWeight: 600 }}>{error}</Paper>
      )}

      <Paper elevation={0} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
          }}>
            <CircularProgress />
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              {/* Group header row */}
              <TableRow>
                {GROUPS.map((g) => (
                  <TableCell
                    key={g.label}
                    colSpan={g.keys.length}
                    align="center"
                    sx={{
                      background: g.bg,
                      fontWeight: 700,
                      borderBottom: '2px solid #cbd5e1',
                      top: 0,
                      ...(g.label === 'Identity' ? { position: 'sticky', left: 0, zIndex: 5 } : {}),
                    }}
                  >
                    {g.label}
                  </TableCell>
                ))}
              </TableRow>
              {/* Column header row */}
              <TableRow>
                {columns.map((col) => {
                  const sticky = stickyCellSx(col.key, true);
                  return (
                    <TableCell
                      key={col.key}
                      align={TEXT_KEYS.has(col.key) ? 'left' : 'right'}
                      sx={{
                        ...HEAD_CELL_SX,
                        background: sticky?.background || groupBgFor(col.key),
                        top: 37, // sit below the group row
                        ...(sticky || {}),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length || 1} align="center" sx={{ py: 8, border: 'none' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ReceiptLongIcon />
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No employees found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.employee_no} sx={TABLE_ROW_SX}>
                  {columns.map((col) => {
                    const sticky = stickyCellSx(col.key, false);
                    return (
                      <TableCell
                        key={col.key}
                        align={TEXT_KEYS.has(col.key) ? 'left' : 'right'}
                        sx={{
                          ...BODY_CELL_SX,
                          whiteSpace: 'nowrap',
                          ...(sticky || {}),
                        }}
                      >
                        {renderCell(col.key, row[col.key])}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
};

export default DetailedSalaryReport;
